import React from "react";
import { IAnimationConfig, ITourTab, TabType } from "./HologramTour.types";
import {
  ConnectLogo,
  GlobeMapIcon,
  NetworkLogo,
  OfficeIcon,
  ProtectLogo,
} from "@ubnt/icons";

// Video and image imports
import networkTabletMp4 from "./assets/tour-videos/network-tablet.mp4";
import networkTablet1080Mp4 from "./assets/tour-videos/network-tablet-1080.mp4";
import networkTabletJpg from "./assets/tour-videos/network-tablet.jpg";
import networkIphoneMp4 from "./assets/tour-videos/network-iphone.mp4";
import networkIphoneJpg from "./assets/tour-videos/network-iphone.jpg";
import hologramNetworkMp4 from "./assets/tour-videos/hologram-network.mp4";
import hologramNetworkSmallMp4 from "./assets/tour-videos/hologram-network-small.mp4";
import hologramNetworkJpg from "./assets/tour-videos/hologram-network.jpg";

import protectTabletMp4 from "./assets/tour-videos/protect-tablet.mp4";
import protectTablet1080Mp4 from "./assets/tour-videos/protect-tablet-1080.mp4";
import protectTabletJpg from "./assets/tour-videos/protect-tablet.jpg";
import protectIphoneMp4 from "./assets/tour-videos/protect-iphone.mp4";
import protectIphoneJpg from "./assets/tour-videos/protect-iphone.jpg";
import hologramProtectMp4 from "./assets/tour-videos/hologram-protect.mp4";
import hologramProtectSmallMp4 from "./assets/tour-videos/hologram-protect-small.mp4";
import hologramProtectJpg from "./assets/tour-videos/hologram-protect.jpg";

import integrationsTabletMp4 from "./assets/tour-videos/integrations-tablet.mp4";
import integrationsTablet1080Mp4 from "./assets/tour-videos/integrations-tablet-1080.mp4";
import integrationsTabletJpg from "./assets/tour-videos/integrations-tablet.jpg";
import integrationsIphoneMp4 from "./assets/tour-videos/integrations-iphone.mp4";
import integrationsIphoneJpg from "./assets/tour-videos/integrations-iphone.jpg";
import hologramIntegrationsMp4 from "./assets/tour-videos/hologram-integrations.mp4";
import hologramIntegrationsSmallMp4 from "./assets/tour-videos/hologram-integrations-small.mp4";
import hologramIntegrationsJpg from "./assets/tour-videos/hologram-integrations.jpg";

import multisiteTabletMp4 from "./assets/tour-videos/multisite-tablet.mp4";
import multisiteTablet1080Mp4 from "./assets/tour-videos/multisite-tablet-1080.mp4";
import multisiteTabletJpg from "./assets/tour-videos/multisite-tablet.jpg";
import multisiteMobileMp4 from "./assets/tour-videos/multisite-mobile.mp4";
import multisiteMobileJpg from "./assets/tour-videos/multisite-mobile.jpg";
import hologramMultisiteMp4 from "./assets/tour-videos/hologram-multisite.mp4";
import hologramMultisiteSmallMp4 from "./assets/tour-videos/hologram-multisite-small.mp4";
import hologramMultisiteJpg from "./assets/tour-videos/hologram-multisite.jpg";

import organizationsTabletMp4 from "./assets/tour-videos/organizations-tablet.mp4";
import organizationsTablet1080Mp4 from "./assets/tour-videos/organizations-tablet-1080.mp4";
import organizationsTabletJpg from "./assets/tour-videos/organizations-tablet.jpg";
import organizationsMobileMp4 from "./assets/tour-videos/organizations-mobile.mp4";
import organizationsMobileJpg from "./assets/tour-videos/organizations-mobile.jpg";
import hologramOrganizationsMp4 from "./assets/tour-videos/hologram-organizations.mp4";
import hologramOrganizationsSmallMp4 from "./assets/tour-videos/hologram-organizations-small.mp4";
import hologramOrganizationsJpg from "./assets/tour-videos/hologram-organizations.jpg";

export const PRODUCT_TAB_GROUP = [
  {
    label: "commons.networking",
    id: TabType.Network,
    playbackTimeInSeconds: 11.03,
    icon: (
      <NetworkLogo variant="flat" size="original" motif="light" actionable />
    ),
  },
  {
    label: "commons.physicalSecurity",
    id: TabType.Protect,
    playbackTimeInSeconds: 9.6,
    icon: (
      <ProtectLogo variant="flat" size="original" motif="light" actionable />
    ),
  },
  {
    label: "commons.integrations",
    id: TabType.Integrations,
    playbackTimeInSeconds: 9.6,
    icon: (
      <ConnectLogo variant="flat" size="original" motif="light" actionable />
    ),
  },
  {
    label: "landing.tour.tabs.multisite.label",
    id: TabType.MultiSite,
    playbackTimeInSeconds: 11.76,
    icon: (
      <GlobeMapIcon variant="fill" size="original" motif="light" actionable />
    ),
  },
  {
    label: "landing.tour.tabs.organizations.title",
    id: TabType.Organizations,
    playbackTimeInSeconds: 10.3,
    icon: (
      <OfficeIcon variant="fill" size="original" motif="light" actionable />
    ),
  },
] satisfies Array<ITourTab>;

export const ANIMATIONS_BY_TAB_TYPE = {
  [TabType.Network]: {
    desktop: {
      src: networkTabletMp4,
      srcMobile: networkTablet1080Mp4,
      poster: networkTabletJpg,
    },
    mobile: {
      src: networkIphoneMp4,
      poster: networkIphoneJpg,
    },
    hologram: {
      src: hologramNetworkMp4,
      srcMobile: hologramNetworkSmallMp4,
      poster: hologramNetworkJpg,
    },
  },
  [TabType.Protect]: {
    desktop: {
      src: protectTabletMp4,
      srcMobile: protectTablet1080Mp4,
      poster: protectTabletJpg,
    },
    mobile: {
      src: protectIphoneMp4,
      poster: protectIphoneJpg,
    },
    hologram: {
      src: hologramProtectMp4,
      srcMobile: hologramProtectSmallMp4,
      poster: hologramProtectJpg,
    },
  },
  [TabType.Integrations]: {
    desktop: {
      src: integrationsTabletMp4,
      srcMobile: integrationsTablet1080Mp4,
      poster: integrationsTabletJpg,
    },
    mobile: {
      src: integrationsIphoneMp4,
      poster: integrationsIphoneJpg,
    },
    hologram: {
      src: hologramIntegrationsMp4,
      srcMobile: hologramIntegrationsSmallMp4,
      poster: hologramIntegrationsJpg,
    },
  },
  [TabType.MultiSite]: {
    desktop: {
      src: multisiteTabletMp4,
      srcMobile: multisiteTablet1080Mp4,
      poster: multisiteTabletJpg,
    },
    mobile: {
      src: multisiteMobileMp4,
      poster: multisiteMobileJpg,
    },
    hologram: {
      src: hologramMultisiteMp4,
      srcMobile: hologramMultisiteSmallMp4,
      poster: hologramMultisiteJpg,
    },
  },
  [TabType.Organizations]: {
    desktop: {
      src: organizationsTabletMp4,
      srcMobile: organizationsTablet1080Mp4,
      poster: organizationsTabletJpg,
    },
    mobile: {
      src: organizationsMobileMp4,
      poster: organizationsMobileJpg,
    },
    hologram: {
      src: hologramOrganizationsMp4,
      srcMobile: hologramOrganizationsSmallMp4,
      poster: hologramOrganizationsJpg,
    },
  },
} satisfies Record<TabType, IAnimationConfig>;
